import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const LABEL = { cc: "Credit Card", cash: "Cash" };
const STATUS_CLS = {
  pending: "bg-brand-gold/15 text-brand-gold-dark border-brand-gold/40",
  approved: "bg-brand-green/15 text-brand-green border-brand-green/40",
  used: "bg-brand-charcoal/10 text-brand-charcoal border-brand-charcoal/30",
  declined: "bg-red-50 text-red-700 border-red-200",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const Row = ({ r, onDecide, busy }) => (
  <li data-testid={`pay-approval-row-${r.id}`} className="rounded-2xl bg-white border border-black/5 p-4 sm:p-5 flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-bold text-[14px] text-brand-charcoal">{r.organization || r.user_email}</p>
        <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${STATUS_CLS[r.status]}`}>{r.status}</span>
      </div>
      <p className="mt-1 text-[12.5px] text-brand-charcoal/60">
        {r.user_name} · {r.user_email} · wants <b>{LABEL[r.method]}</b> · {fmt(r.created_at)}
      </p>
      {r.note && <p className="mt-1 text-[12.5px] text-brand-charcoal/80 italic">&ldquo;{r.note}&rdquo;</p>}
      {r.decided_by && <p className="mt-1 text-[11px] text-brand-charcoal/45">Decided {fmt(r.decided_at)} by {r.decided_by}</p>}
    </div>
    {r.status === "pending" && (
      <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={() => onDecide(r, "approve")} data-testid={`pay-approval-approve-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-charcoal transition-colors disabled:opacity-50">
          <CheckCircle2 className="h-3.5 w-3.5" /> Approve
        </button>
        <button type="button" disabled={busy} onClick={() => onDecide(r, "decline")} data-testid={`pay-approval-decline-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-600 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-50 transition-colors disabled:opacity-50">
          <XCircle className="h-3.5 w-3.5" /> Decline
        </button>
      </div>
    )}
  </li>
);

export default function PaymentApprovalsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payment-method-requests");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDecide = async (r, action) => {
    const reason = action === "decline" ? window.prompt("Optional note to the agency:", "") : null;
    if (action === "decline" && reason === null) return;
    setBusy(r.id);
    try {
      await api.post(`/admin/payment-method-requests/${r.id}/decide`, { action, reason: reason || null });
      toast.success(action === "approve" ? "Approved for one order" : "Declined");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(null);
    }
  };

  const pending = items.filter((r) => r.status === "pending");
  return (
    <div data-testid="payment-approvals-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[12.5px] text-brand-charcoal/60 flex items-center gap-2">
          <Clock className="h-4 w-4" /> Agencies pay by Purchase Order unless you approve card or cash here (one order per approval).
        </p>
        <button type="button" onClick={load} data-testid="pay-approvals-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal transition-colors">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {loading ? (
        <p className="text-[13px] text-brand-charcoal/55">Loading…</p>
      ) : items.length === 0 ? (
        <p data-testid="pay-approvals-empty" className="text-[13px] text-brand-charcoal/55">No payment method requests yet.</p>
      ) : (
        <>
          <p data-testid="pay-approvals-pending-count" className="text-[12px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/70">{pending.length} waiting</p>
          <ul className="space-y-3">{items.map((r) => <Row key={r.id} r={r} onDecide={onDecide} busy={busy === r.id} />)}</ul>
        </>
      )}
    </div>
  );
}
